import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  getClientReviewContext,
  submitClientReview,
} from "../../api/clientReviews";

const StarIcon = ({ filled }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    className={`h-5 w-5 ${filled ? "text-amber-400" : "text-slate-300"}`}
  >
    <path
      d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.955a1 1 0 0 0 .95.69h4.158c.969 0 1.371 1.24.588 1.81l-3.363 2.444a1 1 0 0 0-.364 1.118l1.287 3.955c.3.921-.755 1.688-1.54 1.118l-3.364-2.444a1 1 0 0 0-1.175 0l-3.364 2.444c-.784.57-1.838-.197-1.539-1.118l1.287-3.955a1 1 0 0 0-.364-1.118L2.05 9.382c-.783-.57-.38-1.81.588-1.81h4.159a1 1 0 0 0 .95-.69l1.302-3.955Z"
    />
  </svg>
);

const StarDisplay = ({ value }) => (
  <div className="flex items-center gap-1">
    {Array.from({ length: 5 }).map((_, idx) => (
      <StarIcon key={idx} filled={idx < value} />
    ))}
    <span className="text-xs text-slate-500">{value}/5</span>
  </div>
);

const DEFAULT_REVIEW_QUESTION_CONFIG = {
  overall: { label: "Overall experience", enabled: true, required: true },
  salon: { label: "Salon atmosphere", enabled: true, required: true },
  reception: { label: "Reception", enabled: true, required: true },
  service: { label: "Service quality", enabled: true, required: true },
  staff: { label: "Staff professionalism", enabled: true, required: true },
  online_booking: { label: "Online booking", enabled: true, required: false },
  technician_service: {
    label: "Service quality",
    enabled: true,
    required: false,
  },
  technician_staff: {
    label: "Staff professionalism",
    enabled: true,
    required: false,
  },
};

const normalizeReviewQuestionConfig = (value) => {
  const source = value && typeof value === "object" ? value : {};
  return Object.fromEntries(
    Object.entries(DEFAULT_REVIEW_QUESTION_CONFIG).map(([key, defaults]) => {
      const incoming =
        source[key] && typeof source[key] === "object" ? source[key] : {};
      const required = Boolean(incoming.required ?? defaults.required);
      return [
        key,
        {
          ...defaults,
          label: String(incoming.label || defaults.label).trim() || defaults.label,
          enabled: required || Boolean(incoming.enabled ?? defaults.enabled),
          required,
        },
      ];
    })
  );
};

const StarInputRow = ({
  label,
  value,
  onChange,
  allowClear = false,
  required = false,
}) => (
  <div className="space-y-2">
    <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
      <span>{label}</span>
      {required && <span className="text-xs font-normal text-rose-500">Required</span>}
    </div>
    <div className="flex flex-wrap items-center gap-2">
      {Array.from({ length: 5 }).map((_, idx) => {
        const ratingValue = idx + 1;
        return (
          <button
            key={ratingValue}
            type="button"
            onClick={() => onChange(ratingValue)}
            className="focus:outline-none focus:ring-2 focus:ring-slate-300 rounded"
            aria-label={`${label} ${ratingValue} star`}
          >
            <StarIcon filled={ratingValue <= (value || 0)} />
          </button>
        );
      })}
      <span className="text-xs text-slate-500">
        {value ? `${value}/5` : "Select"}
      </span>
      {allowClear && (
        <button
          type="button"
          onClick={() => onChange(null)}
          className="text-xs font-semibold text-slate-500 hover:text-slate-700"
        >
          Not applicable
        </button>
      )}
    </div>
  </div>
);

export default function PortalReview() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [overall, setOverall] = useState(null);
  const [salonRating, setSalonRating] = useState(null);
  const [receptionRating, setReceptionRating] = useState(null);
  const [serviceRating, setServiceRating] = useState(null);
  const [staffRating, setStaffRating] = useState(null);
  const [onlineBookingRating, setOnlineBookingRating] = useState(null);
  const [comment, setComment] = useState("");
  const [allowContact, setAllowContact] = useState(true);
  const [requestCallback, setRequestCallback] = useState(false);
  const [preferredCallbackTime, setPreferredCallbackTime] = useState("");
  const [technicianRatings, setTechnicianRatings] = useState([]);
  const [publicPromptId, setPublicPromptId] = useState(null);
  const [showPublicPrompt, setShowPublicPrompt] = useState(false);
  const [rewardPoints, setRewardPoints] = useState(null);

  const isSubmitted = Boolean(context?.submitted_at);
  const showGoogleCta = Boolean(context?.show_google_cta);
  const googleReviewUrl = context?.google_review_url || "";
  const showPublicCta = Boolean(showPublicPrompt && publicPromptId);
  const questionConfig = useMemo(
    () => normalizeReviewQuestionConfig(context?.question_config),
    [context?.question_config]
  );
  const questionEnabled = (key) => questionConfig[key]?.enabled !== false;
  const questionRequired = (key) => Boolean(questionConfig[key]?.required);
  const questionLabel = (key) =>
    questionConfig[key]?.label || DEFAULT_REVIEW_QUESTION_CONFIG[key]?.label;
  const commentEnabled = context?.comment_enabled !== false;
  const commentLabel = context?.comment_label || "Additional comments";
  const commentRequiredBelow = Number(context?.comment_required_below_rating || 4);
  const commentRequiredMessage =
    context?.comment_required_message ||
    "We're sorry it wasn't perfect - could you share a quick note so we can improve?";
  const thankYouTitle =
    context?.thank_you_title || "Feedback received. Thank you!";
  const thankYouMessage =
    context?.thank_you_message || "Thank you - your feedback was sent to the salon.";
  const publicReviewCtaText =
    context?.public_review_cta_text || "Leave a public review";
  const publicReviewHelperText =
    context?.public_review_helper_text ||
    "If you have a moment, we would appreciate a public review.";

  const visitDateLabel = useMemo(() => {
    if (!context?.visit_date) return "--";
    return context.visit_date;
  }, [context?.visit_date]);

  useEffect(() => {
    if (!token) {
      setError("Missing review token.");
      return;
    }
    setPublicPromptId(null);
    setShowPublicPrompt(false);
    setRewardPoints(null);
    setLoading(true);
    setError("");
    getClientReviewContext(token)
      .then((data) => {
        setContext(data || null);
        setOverall(data?.overall_rating ?? null);
        setSalonRating(data?.salon_rating ?? null);
        setReceptionRating(data?.reception_rating ?? null);
        setServiceRating(data?.service_rating ?? null);
        setStaffRating(data?.staff_rating ?? null);
        setOnlineBookingRating(data?.online_booking_rating ?? null);
        setComment(data?.comment || "");
        setAllowContact(data?.allow_contact !== false);
        const techs = Array.isArray(data?.technicians) ? data.technicians : [];
        setTechnicianRatings(
          techs.map((tech) => ({
            technician_user_id: tech.technician_user_id,
            technician_name: tech.technician_name || "",
            service_rating: null,
            staff_rating: null,
            comment: "",
          }))
        );
      })
      .catch((err) => {
        setError(err?.response?.data?.detail || "Unable to load review.");
        setContext(null);
      })
      .finally(() => setLoading(false));
  }, [token]);

  const updateTechnicianRating = (techId, updates) => {
    if (!techId) return;
    setTechnicianRatings((prev) =>
      prev.map((entry) =>
        entry.technician_user_id === techId ? { ...entry, ...updates } : entry
      )
    );
  };

  const handleSubmit = async () => {
    if (submitting || !token) return;
    setSubmitError("");
    const requiredMissing =
      !overall ||
      (questionEnabled("salon") && questionRequired("salon") && !salonRating) ||
      (questionEnabled("reception") &&
        questionRequired("reception") &&
        !receptionRating) ||
      (questionEnabled("service") &&
        questionRequired("service") &&
        !serviceRating) ||
      (questionEnabled("staff") && questionRequired("staff") && !staffRating) ||
      (questionEnabled("online_booking") &&
        questionRequired("online_booking") &&
        !onlineBookingRating);
    const technicianRequiredMissing = (technicianRatings || []).some(
      (tech) =>
        (questionEnabled("technician_service") &&
          questionRequired("technician_service") &&
          !tech.service_rating) ||
        (questionEnabled("technician_staff") &&
          questionRequired("technician_staff") &&
          !tech.staff_rating)
    );
    if (requiredMissing || technicianRequiredMissing) {
      setSubmitError("Please rate all required categories before submitting.");
      return;
    }
    if (
      commentEnabled &&
      overall &&
      overall < commentRequiredBelow &&
      !comment.trim()
    ) {
      setSubmitError(commentRequiredMessage);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        overall_rating: overall,
        salon_rating: questionEnabled("salon") ? salonRating : null,
        reception_rating: questionEnabled("reception") ? receptionRating : null,
        service_rating: questionEnabled("service") ? serviceRating : null,
        staff_rating: questionEnabled("staff") ? staffRating : null,
        online_booking_rating: questionEnabled("online_booking")
          ? onlineBookingRating ?? null
          : null,
        comment: commentEnabled ? comment?.trim() || null : null,
        allow_contact: Boolean(allowContact),
        request_callback: Boolean(requestCallback),
        preferred_callback_time: preferredCallbackTime?.trim() || null,
      };
      const technicianPayload = (Array.isArray(technicianRatings)
        ? technicianRatings
        : []
      )
        .map((entry) => ({
          technician_user_id: entry.technician_user_id,
          service_rating: questionEnabled("technician_service")
            ? entry.service_rating ?? null
            : null,
          staff_rating: questionEnabled("technician_staff")
            ? entry.staff_rating ?? null
            : null,
          comment: entry.comment?.trim() || null,
        }))
        .filter(
          (entry) =>
            entry.service_rating != null ||
            entry.staff_rating != null ||
            entry.comment
        );
      if (technicianPayload.length) {
        payload.technician_ratings = technicianPayload;
      }
      const response = await submitClientReview(token, payload);
      setContext((prev) => ({
        ...(prev || {}),
        submitted_at: response?.submitted_at || new Date().toISOString(),
        overall_rating: response?.overall_rating || overall,
        show_google_cta: response?.show_google_cta ?? false,
        google_review_url: response?.google_review_url || prev?.google_review_url,
        show_public_prompt: response?.show_public_prompt ?? false,
        public_prompt_id: response?.public_prompt_id || null,
      }));
      setPublicPromptId(response?.public_prompt_id || null);
      setShowPublicPrompt(Boolean(response?.show_public_prompt));
      setRewardPoints(
        Number.isFinite(response?.reward_points)
          ? Number(response.reward_points)
          : null
      );
    } catch (err) {
      setSubmitError(err?.response?.data?.detail || "Unable to submit feedback.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 px-4 py-8">
      <div className="mx-auto w-full max-w-3xl">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4">
            <h1 className="text-xl md:text-2xl font-semibold text-white">
              {context?.form_title || "Client Feedback"}
            </h1>
            <p className="text-sm text-rose-100">
              {context?.form_subtitle ||
                "Thank you for taking a moment to share your experience."}
            </p>
          </div>
          <div className="px-6 py-6 space-y-6">
            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm md:text-base">
                {error}
              </div>
            )}
            {loading && (
              <div className="text-sm text-slate-500">Loading review...</div>
            )}

            {!loading && !error && context && (
              <>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-1">
                  <div className="text-sm text-slate-500">Salon</div>
                  <div className="text-lg font-semibold text-slate-900">
                    {context.salon_name || "Salon"}
                  </div>
                  <div className="text-sm text-slate-600">
                    Visit date: {visitDateLabel}
                  </div>
                </div>

                {isSubmitted ? (
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 space-y-3">
                    <div className="text-lg font-semibold text-emerald-900">
                      {thankYouTitle}
                    </div>
                    <div className="text-sm text-emerald-800">{thankYouMessage}</div>
                    {context?.overall_rating ? (
                      <StarDisplay value={context.overall_rating} />
                    ) : null}
                    {rewardPoints ? (
                      <div className="rounded-full bg-amber-100 px-4 py-2 text-xs font-semibold text-amber-800">
                        Thanks! You earned {rewardPoints} points.
                      </div>
                    ) : null}
                    {showPublicCta && (
                      <div className="space-y-2">
                        <div className="text-sm text-emerald-800">
                          {publicReviewHelperText}
                        </div>
                        <a
                          href={`/r/public-review/${publicPromptId}`}
                          className="inline-flex items-center justify-center rounded-full bg-rose-500 px-5 py-2 text-sm font-semibold text-white hover:bg-rose-600"
                        >
                          {publicReviewCtaText}
                        </a>
                      </div>
                    )}
                    {!showPublicCta && showGoogleCta && googleReviewUrl && (
                      <div className="space-y-2">
                        <div className="text-sm text-emerald-800">
                          {publicReviewHelperText}
                        </div>
                        <a
                          href={googleReviewUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center justify-center rounded-full bg-rose-500 px-5 py-2 text-sm font-semibold text-white hover:bg-rose-600"
                        >
                          {publicReviewCtaText}
                        </a>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-6">
                    {submitError && (
                      <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm md:text-base">
                        {submitError}
                      </div>
                    )}
                    <StarInputRow
                      label={questionLabel("overall")}
                      value={overall}
                      onChange={setOverall}
                      required
                    />
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      {questionEnabled("salon") && (
                        <StarInputRow
                          label={questionLabel("salon")}
                          value={salonRating}
                          onChange={setSalonRating}
                          required={questionRequired("salon")}
                        />
                      )}
                      {questionEnabled("reception") && (
                        <StarInputRow
                          label={questionLabel("reception")}
                          value={receptionRating}
                          onChange={setReceptionRating}
                          required={questionRequired("reception")}
                        />
                      )}
                      {questionEnabled("service") && (
                        <StarInputRow
                          label={questionLabel("service")}
                          value={serviceRating}
                          onChange={setServiceRating}
                          required={questionRequired("service")}
                        />
                      )}
                      {questionEnabled("staff") && (
                        <StarInputRow
                          label={questionLabel("staff")}
                          value={staffRating}
                          onChange={setStaffRating}
                          required={questionRequired("staff")}
                        />
                      )}
                      {questionEnabled("online_booking") && (
                        <StarInputRow
                          label={questionLabel("online_booking")}
                          value={onlineBookingRating}
                          onChange={setOnlineBookingRating}
                          allowClear
                          required={questionRequired("online_booking")}
                        />
                      )}
                    </div>
                    {technicianRatings.length > 0 &&
                      (questionEnabled("technician_service") ||
                        questionEnabled("technician_staff")) && (
                      <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-4">
                        <div className="text-sm font-semibold text-slate-700">
                          Rate your technician(s)
                        </div>
                        <div className="space-y-4">
                          {technicianRatings.map((tech) => (
                            <div
                              key={tech.technician_user_id}
                              className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3"
                            >
                              <div className="text-sm font-semibold text-slate-900">
                                {tech.technician_name || "Technician"}
                              </div>
                              {Array.isArray(tech.service_names) &&
                                tech.service_names.length > 0 && (
                                  <div className="text-xs text-slate-600">
                                    Service performed: {tech.service_names.join(", ")}
                                  </div>
                                )}
                              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                {questionEnabled("technician_service") && (
                                  <StarInputRow
                                    label={questionLabel("technician_service")}
                                    value={tech.service_rating}
                                    onChange={(value) =>
                                      updateTechnicianRating(
                                        tech.technician_user_id,
                                        { service_rating: value }
                                      )
                                    }
                                    required={questionRequired("technician_service")}
                                  />
                                )}
                                {questionEnabled("technician_staff") && (
                                  <StarInputRow
                                    label={questionLabel("technician_staff")}
                                    value={tech.staff_rating}
                                    onChange={(value) =>
                                      updateTechnicianRating(
                                        tech.technician_user_id,
                                        { staff_rating: value }
                                      )
                                    }
                                    required={questionRequired("technician_staff")}
                                  />
                                )}
                              </div>
                              <div className="space-y-2">
                                <div className="text-sm font-semibold text-slate-700">
                                  Comment (optional)
                                </div>
                                <textarea
                                  value={tech.comment || ""}
                                  onChange={(event) =>
                                    updateTechnicianRating(
                                      tech.technician_user_id,
                                      { comment: event.target.value }
                                    )
                                  }
                                  rows={3}
                                  className="w-full rounded-xl border border-slate-300 px-4 py-3 text-base focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                                  placeholder="Share feedback for this technician."
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {commentEnabled && (
                      <div className="space-y-2">
                        <div className="text-sm font-semibold text-slate-700">
                          {commentLabel}
                          {overall && overall < commentRequiredBelow ? (
                            <span className="ml-2 text-xs font-normal text-rose-500">
                              Required
                            </span>
                          ) : null}
                        </div>
                        <textarea
                          value={comment}
                          onChange={(event) => setComment(event.target.value)}
                          rows={4}
                          className="w-full rounded-xl border border-slate-300 px-4 py-3 text-base focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                          placeholder="Share anything else you would like us to know."
                        />
                        {overall && overall < commentRequiredBelow ? (
                          <div className="text-xs text-slate-500">
                            {commentRequiredMessage}
                          </div>
                        ) : null}
                      </div>
                    )}
                    <label className="flex items-start gap-3 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={allowContact}
                        onChange={(event) => setAllowContact(event.target.checked)}
                        className="mt-1 h-4 w-4 rounded border-slate-300 text-slate-900"
                      />
                      <span>
                        It is okay for the salon to contact me about this feedback.
                      </span>
                    </label>
                    <label className="flex items-start gap-3 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={requestCallback}
                        onChange={(event) => setRequestCallback(event.target.checked)}
                        className="mt-1 h-4 w-4 rounded border-slate-300 text-slate-900"
                      />
                      <span>Request a manager call.</span>
                    </label>
                    {requestCallback && (
                      <div className="space-y-2">
                        <div className="text-sm font-semibold text-slate-700">
                          Preferred callback time
                        </div>
                        <input
                          type="text"
                          value={preferredCallbackTime}
                          onChange={(event) => setPreferredCallbackTime(event.target.value)}
                          className="w-full rounded-xl border border-slate-300 px-4 py-3 text-base focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                          placeholder="e.g. Weekdays after 5pm"
                        />
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={handleSubmit}
                      disabled={submitting}
                      className="inline-flex items-center justify-center rounded-full bg-rose-500 px-6 py-3 text-sm md:text-base font-semibold text-white hover:bg-rose-600 disabled:opacity-60"
                    >
                      {submitting ? "Submitting..." : "Submit feedback"}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
